import z from 'zod';
import { ContestType } from '@betterpool/api-types/contests-service';

import { safeJSONParse } from '~/common/utils/safeJSONParse.util';

export const manageSchema = z.object({
  leagueId: z.string().uuid(),
  contestTemplateId: z.string().uuid(),
  contestId: z.string().optional(),
  brand: z.string().default('RYP'),
  allowedTagNames: z.array(z.nativeEnum(ContestType)).optional(),
  operation: z.enum(['create', 'edit', 'restart']).optional(),
  basicInfo: z.object({
    contestName: z.string().optional(),
    description: z
      .string()
      .transform((data) => (data?.trim() === '' ? undefined : data))
      .optional(),
    banner: z
      .union([
        z
          .object({
            url: z.string(),
            id: z.string().uuid(),
          })
          .optional(),
        z.string().optional(),
      ])
      .optional(),
  }),
  form: z.object({
    gameplay: z
      .object({
        schedule: z
          .object({
            optionTags: z
              .union([z.string(), z.array(z.string())])
              .optional()
              .transform((data) => {
                if (!data) {
                  return;
                }
                return Array.isArray(data) ? data : [data];
              })
              .pipe(z.array(z.string()).optional())
              .optional(),
            slateGroups: z
              .union([z.string(), z.array(z.string())])
              .transform((data) => (Array.isArray(data) ? data : [data]))
              .pipe(z.array(z.string()))
              .optional(),
            slateGroupPresets: z.string().optional(),
            availabilityToExclude: z.boolean().optional(),
            slateGroupSubstitutes: z.boolean().optional(),
          })
          .optional(),
      })
      .optional(),
    entriesSettings: z
      .object(
        {
          contestSizeSettings: z.object(
            {
              contestMaxEntries: z.union([z.number(), z.enum(['unlimited', 'custom'])]),
              customMaxEntries: z.number().optional(),
              contestEntryLimit: z.union([z.number(), z.enum(['custom'])]).optional(),
              contestEntryLimitCustom: z.number().optional(),
              payoutFormat: z.string().optional(),
              segmentedPayoutVsOverallRatio: z.number().optional(),
              segmentPayoutFormat: z.string().optional(),
            },
            { required_error: 'Contest size is required' }
          ),
          entryFeeSettings: z.object(
            {
              settings: z.object(
                {
                  entryFee: z.union([z.number(), z.enum(['custom'])]).optional(),
                  customEntryFee: z.number().optional(),
                  mode: z.enum(['self', 'splash']).optional(),
                  rewardsOptIn: z.boolean().optional(),
                  payoutStructure: z.enum(['OVERALL', 'SINGLE_SLATE', 'MULTI_SLATE']).optional(),
                  payoutSchedule: z.number().optional(),
                  payoutSegments: z
                    .record(
                      z.string(),
                      z.object({
                        id: z.string(),
                        label: z.string(),
                        slateGroupIds: z.preprocess(
                          (val) =>
                            typeof val === 'object' && val !== null ? Object.values(val) : val,
                          z.array(z.string())
                        ),
                        sortOrder: z.preprocess(
                          (val) => (typeof val === 'string' ? parseInt(val, 10) : val),
                          z.number()
                        ),
                      })
                    )
                    .optional(),
                  adminOnly: z
                    .object({
                      prizePool: z.number().optional(),
                    })
                    .optional(),
                },
                { required_error: 'Entry fee settings are required' }
              ),
            },
            { required_error: 'Entry fee settings are required' }
          ),
        },
        { required_error: 'Contest entries and fees are required' }
      )
      .transform((data) => ({
        entryFeeSettings: {
          settings: {
            entryFee: (() => {
              if (data.entryFeeSettings.settings.mode === 'self') return 0;
              if (data.entryFeeSettings.settings.entryFee === 'custom')
                return data.entryFeeSettings.settings.customEntryFee;
              return data.entryFeeSettings.settings.entryFee;
            })(),
            payoutStructure: data.entryFeeSettings.settings.payoutStructure,
            payoutSchedule: data.entryFeeSettings.settings.payoutSchedule,
            payoutSegments: data.entryFeeSettings.settings.payoutSegments,
            mode: data.entryFeeSettings.settings.mode,
            rewardsOptIn: data.entryFeeSettings.settings.rewardsOptIn,
            adminOnly: data.entryFeeSettings.settings.adminOnly,
          },
        },
        contestSizeSettings: {
          payoutFormat: safeJSONParse(data.contestSizeSettings.payoutFormat ?? ''),
          ...(data.contestSizeSettings.segmentPayoutFormat && {
            segmentPayoutFormat: safeJSONParse(data.contestSizeSettings.segmentPayoutFormat),
          }),
          maxEntries: (() => {
            if (data.contestSizeSettings.contestMaxEntries === 'unlimited') return 0;
            if (data.contestSizeSettings.contestMaxEntries === 'custom')
              return data.contestSizeSettings.customMaxEntries;
            return data.contestSizeSettings.contestMaxEntries;
          })(),
          contestEntryLimit: (() => {
            if (data.contestSizeSettings.contestEntryLimit === 'custom')
              return data.contestSizeSettings.contestEntryLimitCustom;
            return data.contestSizeSettings.contestEntryLimit;
          })(),
          segmentedPayoutVsOverallRatio: data.contestSizeSettings.segmentedPayoutVsOverallRatio,
        },
      }))
      .pipe(
        z.object({
          contestSizeSettings: z.object({
            contestEntryLimit: z.number({ required_error: 'Contest entry limit is required' }),
            maxEntries: z.number({ required_error: 'Contest size is required' }),
            payoutFormat: z.object({ schedule: z.array(z.number()) }).default({ schedule: [100] }),
            segmentedPayoutVsOverallRatio: z.number().optional(),
            segmentPayoutFormat: z.object({ schedule: z.array(z.number()) }).optional(),
          }),
          entryFeeSettings: z.object({
            settings: z.object({
              entryFee: z.number({ required_error: 'Entry fee is required' }),
              mode: z.enum(['self', 'splash']).optional(),
              rewardsOptIn: z.boolean().optional(),
              payoutStructure: z.enum(['OVERALL', 'SINGLE_SLATE', 'MULTI_SLATE']).optional(),
              payoutSchedule: z.number().optional(),
              payoutSegments: z
                .record(
                  z.string(),
                  z.object({
                    id: z.string(),
                    label: z.string(),
                    slateGroupIds: z.preprocess(
                      (val) => (typeof val === 'object' && val !== null ? Object.values(val) : val),
                      z.array(z.string())
                    ),
                    sortOrder: z.preprocess(
                      (val) => (typeof val === 'string' ? parseInt(val, 10) : val),
                      z.number()
                    ),
                  })
                )
                .optional(),
              adminOnly: z
                .object({
                  prizePool: z.number().optional(),
                })
                .optional(),
            }),
          }),
        })
      )
      .refine(
        (data) => {
          if (data.entryFeeSettings.settings.payoutStructure === 'MULTI_SLATE') {
            const segmentCount = data.entryFeeSettings.settings.payoutSegments
              ? Object.keys(data.entryFeeSettings.settings.payoutSegments).length
              : 0;
            return segmentCount >= 2;
          }
          return true;
        },
        {
          message: 'Must select the number of payout segments',
          path: ['entryFeeSettings', 'settings', 'payoutSegments'],
        }
      ),
    settings: z
      .object({
        general: z
          .object({
            discoverability: z.string().optional(),
            contestEnableChat: z.boolean().optional(),
            pickDeadlineType: z
              .string()
              .optional()
              .transform((data) => {
                if (!data) return undefined;

                if (typeof data === 'string' && data.startsWith('custom_')) {
                  const [, dayLabel, timeLabel] = data.split('_');
                  return {
                    type: 'custom' as const,
                    dayLabel,
                    timeLabel,
                    original: data,
                  };
                }

                return {
                  type: data,
                  original: data,
                };
              })
              .pipe(
                z
                  .object({
                    type: z.string().optional(),
                    dayLabel: z.string().optional(),
                    timeLabel: z.string().optional(),
                    original: z.string().optional(),
                  })
                  .optional()
              ),
            pickGoal: z.enum(['win', 'lose']).optional(),
            weeklyPicks: z.number().optional(),
            missedPickPenalty: z.string().optional(),
            conference: z.array(z.string()).optional(),
            allTop25Games: z
              .object({
                checked: z.boolean(),
                id: z.string(),
              })
              .optional(),
            excludedGames: z
              .object({
                checked: z.boolean().optional(),
                excludedGameIds: z
                  .record(
                    z
                      .record(z.record(z.object({ exclude: z.boolean().optional() })).optional())
                      .optional()
                  )
                  .optional()
                  .transform((data) => {
                    if (!data) {
                      return;
                    }
                    const transformedData = Object.entries(data)
                      .map(([, value2]) => {
                        const stuff = Object.entries(value2);
                        return [stuff[0][0], stuff[0][1]] as [
                          string,
                          (typeof value2)[keyof typeof value2]
                        ];
                      })
                      .reduce((acc, [key, value]) => {
                        const gameIds = Object.entries(value).reduce((acc2, [key2, value2]) => {
                          if (value2.exclude) {
                            acc2.push(key2);
                          }
                          return acc2;
                        }, []);
                        if (gameIds.length) {
                          acc[key] = gameIds;
                        }

                        return acc;
                      }, {} as Record<string, string[]>);
                    const transformerValues = Object.values(transformedData);
                    if (
                      transformerValues.length === 0 ||
                      transformerValues.every((value) => !value.length)
                    ) {
                      return;
                    }

                    return transformedData;
                  })
                  .pipe(z.record(z.array(z.string())).optional())
                  .optional(),
              })
              .optional(),
            pickScoring: z.enum(['STRAIGHT_UP', 'AGAINST_THE_SPREAD']).optional(),
            hookSpreads: z.boolean().optional(),
            pushSpreadOptions: z.enum(['WIN', 'LOSS', 'HALF']).optional(),
            entryDeadline: z.string().optional(),
          })
          .optional(),
        advanced: z
          .object({
            additionalLives: z
              .string()
              .transform((data) => {
                if (data === '0') return null;
                return Number(data);
              })
              .optional(),
            allowRevivals: z.boolean().optional(),
            dropWorst: z.number().optional(),
            tiebreakers: z.boolean().optional(),
            isDoublePicks: z.boolean().optional(),
            doublePicksSlateGroups: z
              .union([z.string().uuid(), z.array(z.string().uuid())])
              .transform((data) => {
                if (typeof data === 'string') {
                  return [data];
                }
                return data;
              })
              .optional(),
            contestTierRulesSettings: z.number().optional(),
            contestReuseLimitGroup: z
              .object({
                pickReuseLimit: z
                  .union([z.string(), z.number()])
                  .transform((data) => (typeof data === 'string' ? data : String(data)))
                  .optional(),
                pickReuseLimitType: z.enum(['schedule', 'segment']).optional(),
              })
              .optional(),
            postSeasonPickReuseReset: z.boolean().optional(),
            weightedScoring: z
              .object({
                weightedPoints: z
                  .record(
                    z.string().uuid(),
                    z.object({
                      point: z.number().optional(),
                    })
                  )
                  .transform((obj) =>
                    Object.fromEntries(Object.entries(obj).sort((a, b) => a[1].point - b[1].point))
                  )
                  .optional(),
                checked: z.boolean().optional(),
              })
              .optional(),
            bestPicks: z
              .object({
                picks: z
                  .record(
                    z.union([z.string(), z.string().uuid()]).transform((data) => {
                      if (data === 'all') return;
                      return data;
                    }),
                    z.object({
                      pick: z.boolean().optional(),
                    })
                  )
                  .optional(),
                checked: z.boolean().optional(),
              })
              .optional(),
            enableDropWorst: z.boolean().optional(),
            winningMultiplier: z
              .object({
                multiplier: z
                  .record(
                    z.string().uuid(),
                    z.object({
                      multiplier: z.number().optional(),
                    })
                  )
                  .transform((obj) =>
                    Object.fromEntries(
                      Object.entries(obj).sort((a, b) => a[1].multiplier - b[1].multiplier)
                    )
                  )
                  .optional(),
                checked: z.boolean().optional(),
              })
              .optional(),
            alternates: z.boolean().optional(),
            splitPotEligible: z.boolean().optional(),
            autoDeclareLastEntrantTeamsRemaining: z.boolean().optional(),
            scoreTieAsWin: z.boolean().optional(),
            autoPicks: z
              .object({
                checked: z.boolean().optional(),
                maxAutoPicks: z
                  .union([z.number(), z.literal('unlimited')])
                  .optional()
                  .transform((data) => {
                    if (data === 'unlimited') return null;
                    return data;
                  }),
              })
              .optional(),
            buyback: z
              .object({
                enabled: z.boolean().optional(),
                settings: z
                  .object({
                    maxBuybacksPerEntry: z.object({ value: z.number() }),
                    deadline: z.object({ value: z.string() }),
                    feeMultiplier: z.object({ value: z.number() }),
                  })
                  .optional(),
              })
              .optional(),
            flexibleDoublePicks: z
              .object({
                settings: z
                  .object({
                    enabled: z.union([z.string(), z.boolean()]).transform((val) => Boolean(val)),
                    slates: z
                      .array(
                        z.object({
                          id: z.string(),
                          slateId: z.string(),
                          percentage: z.number(),
                        })
                      )
                      .optional(),
                  })
                  .optional(),
                enabled: z.union([z.string(), z.boolean()]).transform((val) => Boolean(val)),
              })
              .optional(),
          })
          .optional(),
        admin: z
          .object({
            createdBy: z.string().optional(),
            makeListedAt: z
              .string()
              .optional()
              .transform((data) => {
                if (!data) {
                  return;
                }
                return new Date(data);
              })
              .pipe(z.date().optional()),
            beginnerOnly: z
              .string()
              .transform((data) => data === 'beginner')
              .optional(),
            resizingMode: z.string().optional(),
            country: z.string().optional(),
          })
          .optional(),
      })
      .optional(),
  }),
});
